import React, { useEffect, useRef, useState } from "react";
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  FlyerTemplate,
  FlyerTemplatePage,
  FlyerTemplateQrPlaceholder,
  CreateFlyerTemplateData,
} from "../../services/api";
import {
  useCreateFlyerTemplate,
  useUpdateFlyerTemplate,
  useUploadFlyerTemplateFile,
  useFlyerCategories,
} from "../../hooks/useFlyerTemplates";
import QrPlaceholderEditor, { QrPlaceholderValue } from "./QrPlaceholderEditor";

const AVAILABLE_LANGUAGES = [
  { code: "de", name: "German" },
  { code: "en", name: "English" },
  { code: "it", name: "Italian" },
  { code: "el", name: "Greek" },
];

const AVAILABLE_COUNTRIES = ["DE", "AT", "IT", "CH", "GR"];

const DEFAULT_PLACEHOLDER: QrPlaceholderValue = {
  x: 35,
  y: 60,
  width: 30,
  height: 30,
  safeMargin: 5,
};

const ACCEPTED = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];

const resolveUrl = (url: string) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const bpeBeUrl = process.env.REACT_APP_BPE_BE_URL || "http://localhost:5000";
  return `${bpeBeUrl}${url}`;
};

// Rows written before safeMargin existed can omit it; the editor needs a number.
const normalizePlaceholder = (
  p?: FlyerTemplateQrPlaceholder,
): QrPlaceholderValue | undefined =>
  p ? { ...p, safeMargin: p.safeMargin ?? 0 } : undefined;

interface Props {
  template: FlyerTemplate | null;
  isOpen: boolean;
  onClose: () => void;
}

const emptyForm = (): CreateFlyerTemplateData => ({
  title: "",
  description: "",
  category: "",
  language: "de",
  countries: [],
  tags: [],
  imageUrl: "",
  sourceType: "image",
  hasQr: true,
  // The QR box lives per page and is seeded from the upload, not at birth.
  pages: [],
  isActive: true,
  isFeatured: false,
  displayOrder: 0,
});

const FlyerTemplateFormModal: React.FC<Props> = ({
  template,
  isOpen,
  onClose,
}) => {
  const createMut = useCreateFlyerTemplate();
  const updateMut = useUpdateFlyerTemplate();
  const uploadMut = useUploadFlyerTemplateFile();
  const { data: categories } = useFlyerCategories();
  const fileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<CreateFlyerTemplateData>(emptyForm());
  const [tagsText, setTagsText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
    if (template) {
      const hasQr = template.hasQr !== false;
      // Legacy rows carry no pages[]: rebuild page 1 from the flat mirrors.
      const pages: FlyerTemplatePage[] = template.pages?.length
        ? template.pages
        : [
            {
              imageUrl: template.imageUrl,
              width: template.width,
              height: template.height,
              qrPlaceholder: template.qrPlaceholder,
            },
          ];
      setForm({
        title: template.title,
        description: template.description || "",
        category: template.category,
        language: template.language,
        countries: template.countries || [],
        tags: template.tags || [],
        imageUrl: template.imageUrl,
        pdfUrl: template.pdfUrl,
        sourceType: template.sourceType,
        width: template.width,
        height: template.height,
        // DEVQA-14: loading an hasQr:false template used to drop every
        // stored qrPlaceholder here too — so even boxes the backend still
        // has (kept exactly for this reason, see setNoQr) got wiped out the
        // moment the admin reopened the edit modal, before they even
        // touched the checkbox. Load whatever the row has; hasQr alone
        // still governs whether generateFlyer ever composites a QR.
        qrPlaceholder: normalizePlaceholder(template.qrPlaceholder),
        hasQr,
        pages: pages.map((p) => ({
          ...p,
          qrPlaceholder: normalizePlaceholder(p.qrPlaceholder),
        })),
        isActive: template.isActive,
        isFeatured: template.isFeatured,
        displayOrder: template.displayOrder,
      });
      setTagsText((template.tags || []).join(", "));
    } else {
      setForm(emptyForm());
      setTagsText("");
    }
  }, [template, isOpen]);

  if (!isOpen) return null;

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      toast.error("Only PNG, JPG or PDF files are allowed");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File too large (max 20MB)");
      return;
    }
    try {
      setUploading(true);
      const res = await uploadMut.mutateAsync(file);
      setForm((f) => {
        const uploaded = res.pages?.length
          ? res.pages
          : [
              {
                imageUrl: res.imageUrl,
                width: res.width,
                height: res.height,
                suggestedPlaceholder: res.suggestedPlaceholder,
                placeholderSource: res.placeholderSource,
              },
            ];
        // Use the backend's auto-detected QR placement; admin can fine-tune.
        // A page without a suggestion simply carries no QR box.
        const pages: FlyerTemplatePage[] = uploaded.map((p) => ({
          imageUrl: p.imageUrl,
          width: p.width,
          height: p.height,
          qrPlaceholder: f.hasQr
            ? normalizePlaceholder(p.suggestedPlaceholder)
            : undefined,
          placeholderSource: p.placeholderSource,
        }));
        return {
          ...f,
          imageUrl: res.imageUrl,
          pdfUrl: res.pdfUrl,
          sourceType: res.sourceType,
          width: res.width,
          height: res.height,
          qrPlaceholder: pages[0]?.qrPlaceholder,
          pages,
        };
      });
      setCurrentPage(0);
      toast.success("File uploaded");
    } catch {
      // error toast handled by the hook
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setPagePlaceholder = (
    index: number,
    qrPlaceholder: QrPlaceholderValue | undefined,
  ) => {
    setForm((f) => ({
      ...f,
      pages: f.pages.map((p, i) => (i === index ? { ...p, qrPlaceholder } : p)),
      // The flat qrPlaceholder mirrors page 1 for back-compat.
      qrPlaceholder: index === 0 ? qrPlaceholder : f.qrPlaceholder,
    }));
  };

  const setNoQr = (noQr: boolean) => {
    // DEVQA-14: toggling "No QR" on used to wipe every page's qrPlaceholder,
    // and toggling it back off never restored anything — the admin had to
    // re-detect/re-place every page from scratch. That wipe was never
    // actually necessary: the backend (generateFlyer) already treats
    // hasQr===false as "ignore qrPlaceholder on every page" regardless of
    // whether the field is still populated (see flyer-template.service.ts's
    // `withQr ? pages[i]?.qrPlaceholder : undefined`). So flipping only
    // hasQr — and leaving every page's stored qrPlaceholder/placeholderSource
    // untouched — is both sufficient (a "no QR" template never emits a QR
    // either way) and correct (flipping back restores the exact prior boxes,
    // no separate backup/restore state needed).
    setForm((f) => ({
      ...f,
      hasQr: !noQr,
    }));
  };

  const toggleCountry = (c: string) => {
    setForm((f) => ({
      ...f,
      countries: f.countries.includes(c)
        ? f.countries.filter((x) => x !== c)
        : [...f.countries, c],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.pages.length) {
      toast.error("Please upload a template file");
      return;
    }
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.category.trim()) {
      toast.error("Category is required");
      return;
    }
    // DEVQA-15: hasQr:true with every page's QR box removed used to save
    // silently — the admin UI still says "QR flyer" but generateFlyer has
    // no placeholder to composite into on any page. Backend enforces the
    // same rule (assertHasQrConsistency); this is just the earlier warning.
    if (form.hasQr && !form.pages.some((p) => !!p.qrPlaceholder)) {
      toast.error(
        "This template needs a QR box on at least one page, or mark it as informational (No QR).",
      );
      return;
    }

    const payload: CreateFlyerTemplateData = {
      ...form,
      tags: tagsText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };

    try {
      if (template) {
        await updateMut.mutateAsync({ id: template._id, data: payload });
      } else {
        await createMut.mutateAsync(payload);
      }
      onClose();
    } catch {
      // error toast handled by the hooks
    }
  };

  const saving = createMut.isPending || updateMut.isPending;
  const page = form.pages[currentPage];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onClose}
        />

        <div className="inline-block w-full max-w-4xl px-4 pt-5 pb-4 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 rounded-lg shadow-xl sm:p-6">
          <div className="flex items-start justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {template ? "Edit Flyer Template" : "New Flyer Template"}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: upload + QR placement */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template File (PNG / JPG / PDF)
                  </label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,application/pdf"
                    className="hidden"
                    onChange={handleFile}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:border-primary-500 hover:text-primary-600 transition-colors disabled:opacity-50"
                  >
                    <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                    {uploading
                      ? "Uploading..."
                      : form.pages.length
                        ? "Replace file"
                        : "Click to upload"}
                  </button>
                  {form.sourceType === "pdf" && form.pdfUrl && (
                    <p className="mt-2 flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <DocumentTextIcon className="h-4 w-4 mr-1" />
                      PDF source · preview shown below
                    </p>
                  )}
                </div>

                {form.pages.length > 0 && form.hasQr ? (
                  <div>
                    {form.pages.length > 1 && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Page {currentPage + 1} of {form.pages.length}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setCurrentPage((p) => p - 1)}
                            disabled={currentPage === 0}
                            className="px-2 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Prev
                          </button>
                          <button
                            type="button"
                            onClick={() => setCurrentPage((p) => p + 1)}
                            disabled={currentPage === form.pages.length - 1}
                            className="px-2 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        QR Placement
                      </label>
                      {page.placeholderSource && (
                        <span
                          className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                            page.placeholderSource === "marker"
                              ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                              : page.placeholderSource === "blank"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {page.placeholderSource === "marker"
                            ? "✓ Auto: marker detected"
                            : page.placeholderSource === "blank"
                              ? "✓ Auto: blank area"
                              : "Default position"}
                        </span>
                      )}
                    </div>
                    {page.qrPlaceholder ? (
                      <>
                        <QrPlaceholderEditor
                          key={currentPage}
                          imageUrl={resolveUrl(page.imageUrl)}
                          value={page.qrPlaceholder as QrPlaceholderValue}
                          onChange={(qrPlaceholder) =>
                            setPagePlaceholder(currentPage, qrPlaceholder)
                          }
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setPagePlaceholder(currentPage, undefined)
                          }
                          className="mt-2 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700"
                        >
                          Remove QR from this page
                        </button>
                      </>
                    ) : (
                      <div className="rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                        <img
                          src={resolveUrl(page.imageUrl)}
                          alt={`Page ${currentPage + 1}`}
                          className="block w-full h-auto"
                        />
                        <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-gray-200 dark:border-gray-700">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            No QR on this page
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setPagePlaceholder(currentPage, {
                                ...DEFAULT_PLACEHOLDER,
                              })
                            }
                            className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Add QR to this page
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-48 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-400">
                    {form.pages.length
                      ? "Informational flyer — no QR code will be added"
                      : "Upload a file to define the QR placement"}
                  </div>
                )}
              </div>

              {/* Right: metadata */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, title: e.target.value }))
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="e.g. Summer Cashback Flyer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, description: e.target.value }))
                    }
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Usage information shown to dealers"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category *
                    </label>
                    <input
                      type="text"
                      list="flyer-categories"
                      value={form.category}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, category: e.target.value }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="General Promotion"
                    />
                    <datalist id="flyer-categories">
                      {(categories || []).map((c) => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Language
                    </label>
                    <select
                      value={form.language}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, language: e.target.value }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {AVAILABLE_LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Countries{" "}
                    <span className="text-gray-400 font-normal">
                      (none = all countries)
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_COUNTRIES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCountry(c)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                          form.countries.includes(c)
                            ? "bg-primary-600 text-white border-primary-600"
                            : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600"
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tags{" "}
                    <span className="text-gray-400 font-normal">
                      (comma separated)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={tagsText}
                    onChange={(e) => setTagsText(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="summer, cashback, qr"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={form.displayOrder}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          displayOrder: parseInt(e.target.value, 10) || 0,
                        }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div className="flex items-end gap-4 pb-1">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, isActive: e.target.checked }))
                        }
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Active
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={form.isFeatured}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            isFeatured: e.target.checked,
                          }))
                        }
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Featured
                    </label>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={!form.hasQr}
                    onChange={(e) => setNoQr(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  No QR code (informational flyer)
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || uploading}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving
                  ? "Saving..."
                  : template
                    ? "Save Changes"
                    : "Create Template"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FlyerTemplateFormModal;
